import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout';
import { DashboardPage } from '@/pages/Dashboard';
import { ErrorBoundary } from '@/components/ErrorBoundary';

/**
 * Router declaration with route-level code splitting.
 *
 * Dashboard ships in the main bundle (it's the home page — likely to be
 * visited first). Every other page is loaded on-demand via React.lazy,
 * which splits the bundle and drops initial TTI.
 */

const RunnerPage       = lazy(() => import('@/pages/Runner').then((m) => ({ default: m.RunnerPage })));
const LivePage         = lazy(() => import('@/pages/Live').then((m) => ({ default: m.LivePage })));
const HistoryPage      = lazy(() => import('@/pages/History').then((m) => ({ default: m.HistoryPage })));
const ConfigurationPage = lazy(() => import('@/pages/Configuration').then((m) => ({ default: m.ConfigurationPage })));
const TestsPage        = lazy(() => import('@/pages/Tests').then((m) => ({ default: m.TestsPage })));
const SuitesPage       = lazy(() => import('@/pages/Suites').then((m) => ({ default: m.SuitesPage })));
const SentinelPage     = lazy(() => import('@/pages/Sentinel').then((m) => ({ default: m.SentinelPage })));
const ResultsPage      = lazy(() => import('@/pages/Results').then((m) => ({ default: m.ResultsPage })));
const EditorPage       = lazy(() => import('@/pages/Editor').then((m) => ({ default: m.EditorPage })));
const HelpPage         = lazy(() => import('@/pages/Help').then((m) => ({ default: m.HelpPage })));
const GlossarySection  = lazy(() => import('@/pages/Help').then((m) => ({ default: m.GlossarySection })));

function PageLoader() {
  return (
    <div className="content">
      <div
        className="grid-bg"
        style={{
          padding: 64,
          textAlign: 'center',
          color: 'var(--text-muted)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.1em' }}>
          CHARGEMENT…
        </div>
      </div>
    </div>
  );
}

function withSuspense(node: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true,             element: <ErrorBoundary label="Dashboard"><DashboardPage /></ErrorBoundary> },
      // All lazy pages are wrapped in ErrorBoundary so a failed dynamic import
      // (network hiccup, Vite restart, module crash) shows a recoverable error
      // screen instead of React Router's raw crash page (CLAUDE.md §-1).
      { path: 'run',             element: <ErrorBoundary label="Nouvelle campagne">{withSuspense(<RunnerPage />)}</ErrorBoundary> },
      { path: 'live',            element: <ErrorBoundary label="Suivi en direct">{withSuspense(<LivePage />)}</ErrorBoundary> },
      { path: 'results/:runId',  element: <ErrorBoundary label="Résultats">{withSuspense(<ResultsPage />)}</ErrorBoundary> },
      { path: 'history',         element: <ErrorBoundary label="Historique">{withSuspense(<HistoryPage />)}</ErrorBoundary> },
      { path: 'config',          element: <ErrorBoundary label="Configuration">{withSuspense(<ConfigurationPage />)}</ErrorBoundary> },
      { path: 'tests',           element: <ErrorBoundary label="Tests">{withSuspense(<TestsPage />)}</ErrorBoundary> },
      { path: 'suites',          element: <ErrorBoundary label="Suites">{withSuspense(<SuitesPage />)}</ErrorBoundary> },
      { path: 'sentinel',        element: <ErrorBoundary label="Sentinel">{withSuspense(<SentinelPage />)}</ErrorBoundary> },
      { path: 'editor/new',      element: <ErrorBoundary label="Éditeur">{withSuspense(<EditorPage />)}</ErrorBoundary> },
      { path: 'editor/:tcId',    element: <ErrorBoundary label="Éditeur">{withSuspense(<EditorPage />)}</ErrorBoundary> },

      // Help section (nested routes — HelpPage is the shell)
      {
        path: 'help',
        element: <ErrorBoundary label="Aide">{withSuspense(<HelpPage />)}</ErrorBoundary>,
        children: [
          { index: true,        element: <Navigate to="/help/glossary" replace /> },
          { path: 'glossary',   element: withSuspense(<GlossarySection />) },
        ],
      },

      // Legacy aliases (Jinja2 URLs kept working)
      { path: 'settings', element: <Navigate to="/config" replace /> },
      { path: 'params',   element: <Navigate to="/config" replace /> },
    ],
  },
]);
